// Shared Sanguine resource readers; Contingency authorization lives in contingency-arm.ts.
import { findChargeEntry, type ChargeReaders } from '../charges/charge-adjust.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';

export const SANGUINE_SCHEMA = 'janus.sanguine.truth.v1';
/** FT_SANGUINE_PASSIVE_ID — the shared Sanguine pool every sanguine draws from. */
export const SANGUINE_POOL_ID = '2274AD74-E2F8-47AA-8FD1-7DE4713695B9';
export const SANGUINE_CONTINGENCY_ID = 'DC9778C6-5B9B-1501-B86A-5EEF4671F66A';
export const SPELL_CONTINGENCY_ID = '3BDE1AA4-23D6-C4E8-CADD-ED15B4403EA6';
export const CONTINGENCY_ARMING_IDS: readonly string[] = [SANGUINE_CONTINGENCY_ID, SPELL_CONTINGENCY_ID];
/** FT_CONTINGENCY_MAX_SPELL_LEVEL */
export const CONTINGENCY_MAX_SPELL_LEVEL = 5;

type Row = Record<string, unknown>;
export type GateVerdict = { kind: 'refuse'; reason: string; message: string } | { kind: 'allow' };
const allow: GateVerdict = { kind: 'allow' };
const refuse = (reason: string, message: string): GateVerdict => ({ kind: 'refuse', reason, message });
const intOf = (v: unknown): number | null => typeof v === 'number' ? (Number.isInteger(v) ? v : null)
  : typeof v === 'string' && /^\s*-?\d+\s*$/.test(v) ? parseInt(v, 10) : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** The Sanguine pool the page holds (ability_entry_charges on the pool entry), or null when it cannot tell. */
export function sanguinePoolCurrent(caster: Row, readers: ChargeReaders): number | null {
  const found = findChargeEntry(caster, SANGUINE_POOL_ID, readers);
  return found ? intOf(found.current) : null;
}
/**
 * Could a status the caster holds waive the Sanguine price (ft_ability_charge_spend_waived_by_status(): a status whose definition
 * carries ability_charges_not_required naming the pool)? The dialog's quoted price is from when it OPENED, so a waiver gained since
 * would make a local refusal false (Codex review 2026-09-28 F8). True also when a status definition is not held — the page cannot
 * tell. Contingency uses the exact payload-status waiver in its full arm planner.
 */
export function waiverPossible(caster: Row, readers: ChargeReaders): boolean {
  if (chargeReductionDelta(caster, -1, readers) === 0) return true;
  const statuses = caster.statuses as Row | undefined;
  const list = Array.isArray(statuses?.target) ? statuses!.target as unknown[] : [];
  for (const raw of list) {
    const id = upper((raw as Row | null)?.status_item_id);
    if (!id) return true;
    const def = readers.definition?.(id) ?? null;
    if (!def) return true;
    const waived = readers.entries(def, 'ability_charges_not_required')
      .map(e => upper(typeof e === 'string' ? e : (e as Row | null)?.item_id ?? ((e as Row | null)?.items as Row | undefined)?.item_id));
    if (waived.includes(SANGUINE_POOL_ID) || waived.some(w => !w)) return true;
  }
  return false;
}

/** Does the caster already hold an armed contingency (a status row of either arming item)? null = cannot tell. */
export function holdsContingency(caster: Row): boolean | null {
  const statuses = caster.statuses as Row | undefined;
  if (!statuses || typeof statuses !== 'object') return null;
  const lists = ['target', 'caster'].map(k => statuses[k]).filter(Array.isArray) as unknown[][];
  return lists.some(list => list.some(s => CONTINGENCY_ARMING_IDS.includes(upper((s as Row | null)?.status_item_id))));
}

export { contingencyArmAuthorize as contingencyArmGate } from './contingency-arm.js';

/** ft_sanguine_extraordinary_cast_execute()'s pool pre-check (the list endpoint's charge_cost is already net of a waiver). */
export function extraordinaryCastGate(chargeCost: number, pool: number | null, waiver: boolean): GateVerdict {
  if (!waiver && chargeCost > 0 && pool !== null && pool < chargeCost)
    return refuse('insufficient_sanguine', `Not enough Sanguine charges for that cast (need ${chargeCost}, have ${pool}).`);
  return allow;
}
